import React, { useState } from 'react';
import { Drawing, DrawingFolder, PlanSheet } from '@/app/types/cad';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { ScrollArea } from '@/app/components/ui/scroll-area';
import { 
  FileText, 
  Folder, 
  FolderOpen, 
  Plus, 
  Edit2, 
  Trash2, 
  Copy, 
  ChevronRight, 
  ChevronDown,
  FilePlus,
  Printer
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface DrawingBrowserProps {
  rootFolder: DrawingFolder;
  activeDrawingId?: string;
  activePlanId?: string;
  onDrawingSelect: (drawingId: string) => void;
  onPlanSelect?: (planId: string, drawingId: string) => void;
  onDrawingCreate: (parentFolderId: string, name: string) => void;
  onDrawingRename: (drawingId: string, newName: string) => void;
  onDrawingDelete: (drawingId: string) => void;
  onDrawingDuplicate: (drawingId: string) => void;
  onFolderCreate: (parentFolderId: string, name: string) => void;
  onFolderRename: (folderId: string, newName: string) => void;
  onFolderDelete: (folderId: string) => void;
  onFolderToggle: (folderId: string) => void;
  onItemMove?: (itemId: string, targetFolderId: string) => void;
  onPlanCreate?: (drawingId: string) => void;
  onPlanDelete?: (planId: string, drawingId: string) => void;
}

export function DrawingBrowser({
  rootFolder,
  activeDrawingId,
  activePlanId,
  onDrawingSelect,
  onPlanSelect,
  onDrawingCreate,
  onDrawingRename,
  onDrawingDelete,
  onDrawingDuplicate,
  onFolderCreate,
  onFolderRename,
  onFolderDelete,
  onFolderToggle,
  onItemMove,
  onPlanCreate,
  onPlanDelete
}: DrawingBrowserProps) {
  const { t } = useLanguage();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; item: Drawing | DrawingFolder } | null>(null);
  const [draggedItem, setDraggedItem] = useState<Drawing | DrawingFolder | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [expandedDrawings, setExpandedDrawings] = useState<Set<string>>(new Set());

  const isDrawing = (item: Drawing | DrawingFolder): item is Drawing => {
    return 'blocks' in item;
  };

  const toggleDrawingExpand = (drawingId: string) => {
    setExpandedDrawings(prev => {
      const next = new Set(prev);
      if (next.has(drawingId)) {
        next.delete(drawingId);
      } else {
        next.add(drawingId);
      }
      return next;
    });
  };

  const renderPlans = (drawing: Drawing, depth: number) => {
     if (!drawing.plans || drawing.plans.length === 0) return null;
     
     return drawing.plans.map(plan => {
       const isPlanActive = activePlanId === plan.id;
       return (
         <div 
           key={plan.id}
           className="group flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-black/5"
           style={{
             paddingLeft: `${(depth + 1) * 16 + 8}px`,
             backgroundColor: isPlanActive ? 'var(--md-secondary-container)' : 'transparent',
           }}
           onClick={(e) => {
             e.stopPropagation();
             if (onPlanSelect) onPlanSelect(plan.id, drawing.id);
           }}
         >
           <Printer className="w-3 h-3 text-gray-500" />
           <span className={`text-xs flex-1 truncate ${isPlanActive ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
             {plan.name}
           </span>
           <div className="opacity-0 group-hover:opacity-100 flex gap-1">
             {onPlanDelete && (
                <button
                  className="w-4 h-4 flex items-center justify-center rounded hover:bg-red-100 text-red-500"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm(t('browser.confirmDeletePlan').replace('{name}', plan.name))) {
                       onPlanDelete(plan.id, drawing.id);
                    }
                  }}
                  title={t('browser.deletePlan')}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
             )}
           </div>
         </div>
       );
     });
  };

  const handleRename = (item: Drawing | DrawingFolder) => {
    if (editingName.trim() === '') {
      toast.error(t('browser.validation.nameRequired'));
      return;
    }
    
    if (isDrawing(item)) {
      onDrawingRename(item.id, editingName.trim());
    } else {
      onFolderRename(item.id, editingName.trim());
    }
    
    setEditingId(null);
    setEditingName('');
  };

  const startEdit = (item: Drawing | DrawingFolder) => {
    setEditingId(item.id);
    setEditingName(item.name);
  };

  const renderItem = (item: Drawing | DrawingFolder, depth: number = 0) => {
    const isActive = isDrawing(item) && item.id === activeDrawingId && !activePlanId;
    const isEditing = editingId === item.id;
    const isFolder = !isDrawing(item);
    const folder = isFolder ? item as DrawingFolder : null;
    const drawing = !isFolder ? item as Drawing : null;
    const isExpanded = isDrawing(item) ? expandedDrawings.has(item.id) : false;

    return (
      <div key={item.id}>
        <div
          className="group flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-black/5"
          style={{
            paddingLeft: `${depth * 16 + 8}px`,
            backgroundColor: isActive ? 'var(--md-primary-container)' : 
                            dropTarget === item.id ? 'var(--md-secondary-container)' : 
                            'transparent',
          }}
          onClick={() => {
            if (isDrawing(item) && !isEditing) {
              onDrawingSelect(item.id);
            }
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            setContextMenu({ x: e.clientX, y: e.clientY, item });
          }}
          draggable={true}
          onDragStart={(e) => {
            e.dataTransfer.setData('text/plain', item.id);
            setDraggedItem(item);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (isFolder) {
              setDropTarget(item.id);
            }
          }}
          onDragLeave={() => {
            setDropTarget(null);
          }}
          onDrop={(e) => {
            e.preventDefault();
            if (draggedItem && dropTarget && onItemMove) {
              onItemMove(draggedItem.id, dropTarget);
            }
            setDraggedItem(null);
            setDropTarget(null);
          }}
        >
          {/* Expand/Collapse Icon for folders AND drawings (if they have plans) */}
          {(isFolder || (isDrawing(item) && item.plans && item.plans.length > 0)) && (
            <button
              className="w-4 h-4 flex items-center justify-center"
              onClick={(e) => {
                e.stopPropagation();
                if (isFolder) onFolderToggle(item.id);
                else toggleDrawingExpand(item.id);
              }}
            >
              {(isFolder ? folder?.expanded : isExpanded) ? (
                <ChevronDown className="w-3 h-3" />
              ) : (
                <ChevronRight className="w-3 h-3" />
              )}
            </button>
          )}
          
          {/* Spacing for items without expand button */}
          {!(isFolder || (isDrawing(item) && item.plans && item.plans.length > 0)) && (
             <div className="w-4 h-4" />
          )}

          {/* Icon */}
          <div className="w-4 h-4 flex-shrink-0">
            {isFolder ? (
              folder?.expanded ? (
                <FolderOpen className="w-4 h-4" style={{ color: 'var(--md-on-surface)' }} />
              ) : (
                <Folder className="w-4 h-4" style={{ color: 'var(--md-on-surface)' }} />
              )
            ) : (
              <FileText className="w-4 h-4" style={{ color: 'var(--md-on-surface)' }} />
            )}
          </div>

          {/* Name */}
          {isEditing ? (
            <Input
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              onBlur={() => handleRename(item)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleRename(item);
                } else if (e.key === 'Escape') {
                  setEditingId(null);
                  setEditingName('');
                }
              }}
              className="text-xs h-6 flex-1"
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span
              className="text-xs flex-1 truncate"
              style={{
                color: isActive ? 'var(--md-on-primary-container)' : 'var(--md-on-surface)',
                fontWeight: isActive ? 600 : 400,
              }}
            >
              {item.name}
            </span>
          )}

          {/* Actions */}
          {!isEditing && (
            <div className="opacity-0 group-hover:opacity-100 flex gap-1">
              {isDrawing(item) && onPlanCreate && (
                 <button
                    className="w-5 h-5 flex items-center justify-center rounded hover:bg-black/10"
                    onClick={(e) => {
                       e.stopPropagation();
                       onPlanCreate(item.id);
                       if (!isExpanded) toggleDrawingExpand(item.id);
                    }}
                    title={t('browser.createPlan')}
                 >
                    <Printer className="w-3 h-3" />
                 </button>
              )}
              <button
                className="w-5 h-5 flex items-center justify-center rounded hover:bg-black/10"
                onClick={(e) => {
                  e.stopPropagation();
                  startEdit(item);
                }}
                title={t('common.rename')}
              >
                <Edit2 className="w-3 h-3" />
              </button>
              {isDrawing(item) && (
                <button
                  className="w-5 h-5 flex items-center justify-center rounded hover:bg-black/10"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDrawingDuplicate(item.id);
                  }}
                  title={t('common.duplicate')}
                >
                  <Copy className="w-3 h-3" />
                </button>
              )}
              <button
                className="w-5 h-5 flex items-center justify-center rounded hover:bg-black/10"
                onClick={(e) => {
                  e.stopPropagation();
                  if (isDrawing(item)) {
                    if (window.confirm(t('browser.confirmDeleteDrawing').replace('{name}', item.name))) {
                      onDrawingDelete(item.id);
                    }
                  } else {
                    if (window.confirm(t('browser.confirmDeleteFolder').replace('{name}', item.name))) {
                      onFolderDelete(item.id);
                    }
                  }
                }}
                title={t('common.delete')}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>

        {/* Render children if folder is expanded */}
        {isFolder && folder?.expanded && folder.children.map((child) => renderItem(child, depth + 1))}
        
        {/* Render Plans if drawing is expanded */}
        {isDrawing(item) && isExpanded && renderPlans(item as Drawing, depth)}
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-3 border-b" style={{ borderColor: 'var(--md-outline-variant)' }}>
        <h3 className="font-semibold text-sm mb-3">{t('browser.drawings')}</h3>
        
        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            size="sm"
            className="flex-1 text-xs"
            onClick={() => {
              const name = prompt(t('browser.promptNewDrawingName'));
              if (name && name.trim()) {
                onDrawingCreate(rootFolder.id, name.trim());
              }
            }}
          >
            <FilePlus className="w-3 h-3 mr-1" />
            {t('common.drawing')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="flex-1 text-xs"
            onClick={() => {
              const name = prompt(t('browser.promptNewFolderName'));
              if (name && name.trim()) {
                onFolderCreate(rootFolder.id, name.trim());
              }
            }}
          >
            <Plus className="w-3 h-3 mr-1" />
            {t('common.folder')}
          </Button>
        </div>
      </div>

      {/* Tree View */}
      <ScrollArea className="flex-1">
        <div className="p-2">
          {rootFolder.children.map((item) => renderItem(item, 0))}
          {rootFolder.children.length === 0 && (
            <div className="text-center py-8 text-xs" style={{ color: 'var(--md-on-surface-variant)' }}>
              {t('browser.noDrawings')}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}